/**
 * Ростер кадров HR — один на ОО (HrHub «Сотрудники») и ИО (EO → HR). 03.10 (владелец:
 * «ОО нанимает и увольняет только операторов; ДК, ОО и ИО — только ИО; себя и последнего
 * ИО уволить нельзя»; «увольнение снимает неначатую работу»; «доступ новому — ссылкой
 * в бота»; «V4 — источник правды по найму и увольнению»). Заменил oo/HrView (карточки
 * ~80px, только чтение) и eo/EoHrView (вторая копия того же списка).
 *   • панель 30px: счётчик, фильтр роли, «?», «+ Нанять»; строки 30px, тап — раскрыть;
 *   • уволенные — свёрнутой группой внизу, с датой и «Вернуть»;
 *   • загрузка/ошибка/пусто — внутри списка, панель остаётся на месте.
 * mode="eo" добавляет чипы ролей и выдачу нескольких ролей при найме. Кто кем управляет —
 * canManageRoles из @ub/shared/staffRoles (то же правило проверяет сервер).
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { STAFF_ROLE_LABELS, canManageRoles, hasManagerRole, type StaffRoleId } from "@ub/shared/staffRoles";
import { useAuth } from "../../auth";
import { ConfirmModal, actionPairCss as ap } from "../../components/shared";
import { useStaffAdmin, type Invite, type StaffRow as Row } from "../../hooks/useStaffAdmin";
import { RosterToolbar, type RoleFilter } from "./RosterToolbar";
import { FiredRow, StaffRow } from "./StaffRow";
import { StaffRowDetail, type RowRights } from "./StaffRowDetail";
import { AccessLogSheet } from "./AccessLogSheet";
import { HireSheet } from "./HireSheet";
import { FireDialog } from "./FireDialog";
import css from "./roster.module.css";

/** Кого можно нанять: «Админ зала» как роль устарела (это дневное состояние оператора). */
const HIREABLE: StaffRoleId[] = ["eo", "oo", "dc", "operator"];

const Chev = ({ open }: { open: boolean }) => (
  <svg className={`${css.chev} ${open ? css.chevOpen : ""}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
);

export function StaffRoster({ mode, onChanged }: { mode: "oo" | "eo"; onChanged?: () => void }) {
  const { user } = useAuth();
  const actor = useMemo(() => user?.roles ?? [], [user]);
  const s = useStaffAdmin({ onChanged });
  const [filter, setFilter] = useState<RoleFilter>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [firedOpen, setFiredOpen] = useState(false);
  const [hireOpen, setHireOpen] = useState(false);
  const [firing, setFiring] = useState<Row | null>(null);
  const [rebind, setRebind] = useState<Row | null>(null);
  const [inv, setInv] = useState<{ id: string; invite: Invite } | null>(null);
  const [invBusy, setInvBusy] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [toast, setToast] = useState<{ t: string; err: boolean } | null>(null);
  const toastT = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => () => window.clearTimeout(toastT.current), []);
  const say = useCallback((t: string, err = false) => {
    setToast({ t, err });
    window.clearTimeout(toastT.current);
    toastT.current = window.setTimeout(() => setToast(null), err ? 4200 : 3000);
  }, []);

  const eo = actor.includes("eo");
  const [deniedOpen, setDeniedOpen] = useState(false);   // 13.10 журнал входов: чужие попытки (только ИО)
  const hireRoles = HIREABLE.filter((r) => canManageRoles(actor, [r]));
  const rightsOf = (r: Row): RowRights => ({
    manage: canManageRoles(actor, r.roles),
    edit: eo || (actor.includes("oo") && !hasManagerRole(r.roles)),
    roles: mode === "eo" && eo,
    self: r.id === user?.staffId,
    logs: eo,
    board: eo || actor.includes("oo"),
    history: eo || actor.includes("oo"),
  });

  const { active, fired, counts } = useMemo(() => {
    const counts: Partial<Record<StaffRoleId, number>> = {};
    const active = s.staff.filter((r) => r.status !== "fired");
    for (const r of active) for (const ro of r.roles) counts[ro] = (counts[ro] ?? 0) + 1;
    const fired = s.staff.filter((r) => r.status === "fired").sort((a, b) => (b.firedAt ?? "").localeCompare(a.firedAt ?? ""));
    return { active, fired, counts };
  }, [s.staff]);
  const match = (r: Row) => filter === "all" || r.roles.includes(filter);
  const shown = active.filter(match);
  const firedShown = fired.filter(match);

  /** Раскрыть строку и докрутить её к верху списка (после «Показать»/«Вернуть») — панель видна целиком. */
  const focusRow = (id: string) => {
    setOpenId(id);
    requestAnimationFrame(() => listRef.current?.querySelector(`[data-staff="${CSS.escape(id)}"]`)?.scrollIntoView({ block: "start" }));
  };
  const giveInvite = async (r: Row, unbind = false) => {
    setInvBusy(r.id);
    const res = await s.invite(r.id, unbind);
    setInvBusy(null);
    if (res.ok) { setInv({ id: r.id, invite: res.value }); setOpenId(r.id); } else say(res.error, true);
  };
  /** Вернуть уволенного; null — успех, иначе текст отказа (покажет вызвавший: тост или шит найма). */
  const restore = async (id: string, name: string): Promise<string | null> => {
    setRestoring(id);
    const res = await s.restore(id);
    setRestoring(null);
    if (!res.ok) return res.error;
    setInv(res.value ? { id, invite: res.value } : null);
    focusRow(id);
    say(res.value ? `Возвращён: ${name}. Отправьте ссылку для входа` : `Возвращён: ${name}, вход по прежнему Telegram`);
    return null;
  };

  const body = s.loading ? (
    Array.from({ length: 12 }, (_, i) => (
      <div key={i} className={css.skel}><span className={css.skelBar} style={{ width: `${38 + ((i * 17) % 30)}%` }} /><span className={css.skelBar} style={{ width: "14%" }} /></div>
    ))
  ) : s.error ? (
    <div className={`${css.state} ${css.stateErr}`}>
      <span className={css.stateT}>Не удалось загрузить сотрудников</span>
      <span className={css.stateS}>{s.error}</span>
      <button type="button" className={`${ap.abtn} ${ap.cf} ${ap.solo}`} onClick={s.reload}>Повторить</button>
    </div>
  ) : (
    <>
      {shown.length === 0 ? (
        <div className={css.state}>
          <span className={css.stateT}>{filter === "all" ? "В ростере пока никого" : `Нет сотрудников с ролью «${STAFF_ROLE_LABELS[filter]}»`}</span>
          {filter === "all" && hireRoles.length ? <span className={css.stateS}>«+ Нанять» — добавить первого</span> : null}
        </div>
      ) : shown.map((r) => (
        <Fragment key={r.id}>
          <StaffRow row={r} open={openId === r.id} onToggle={() => setOpenId((o) => (o === r.id ? null : r.id))} />
          {openId === r.id ? (
            <StaffRowDetail row={r} rights={rightsOf(r)} busy={s.busy.has(r.id)} err={s.rowErr[r.id] ?? ""}
              invite={inv?.id === r.id ? inv.invite : null} inviteBusy={invBusy === r.id}
              onStatus={(st) => s.setStatus(r.id, st)} onRole={(ro) => s.toggleRole(r.id, ro)} onPhone={(ph) => s.setPhone(r.id, ph)}
              onProfile={(pt) => s.setProfile(r.id, pt)} onBoardHidden={(h) => s.setBoardHidden(r.id, h)} onHistoryAll={(on) => s.setHistoryAll(r.id, on)}
              onInvite={() => void giveInvite(r)} onRebind={() => setRebind(r)} onFire={() => setFiring(r)} onPoll={s.refreshQuiet} />
          ) : null}
        </Fragment>
      ))}
      {firedShown.length ? (
        <>
          <button type="button" className={css.group} aria-expanded={firedOpen} onClick={() => setFiredOpen((v) => !v)}>
            <Chev open={firedOpen} />Уволенные · {firedShown.length}
          </button>
          {firedOpen ? firedShown.map((r) => (
            <FiredRow key={r.id} row={r} canRestore={canManageRoles(actor, r.roles)} busy={restoring === r.id}
              onRestore={() => void restore(r.id, r.name).then((e) => { if (e) say(e, true); })} />
          )) : null}
        </>
      ) : null}
    </>
  );

  return (
    <div className={css.root}>
      <RosterToolbar mode={mode} total={s.loading || s.error ? null : shown.length} all={active.length} vacation={shown.filter((r) => r.status === "vacation").length} filter={filter} counts={counts}
        onFilter={setFilter} canHire={hireRoles.length > 0} hireOpen={hireOpen} onHire={() => setHireOpen(true)} />
      {eo ? <button type="button" className={css.deniedBtn} onClick={() => setDeniedOpen(true)}>Чужие попытки входа · 14 дней</button> : null}
      {eo ? <AccessLogSheet open={deniedOpen} mode="denied" onClose={() => setDeniedOpen(false)} /> : null}
      <div className={css.list} ref={listRef}>{body}</div>
      <HireSheet open={hireOpen} roleOptions={hireRoles} multiRole={mode === "eo" && eo} onClose={() => setHireOpen(false)} hire={s.hire}
        onRestore={async (d) => {
          /* 03.10 (ревью): шит закрываем только после успеха — отказ сервера не стирает набранную форму */
          setFilter("all");
          const e = await restore(d.id, s.staff.find((r) => r.id === d.id)?.name ?? d.name);
          if (!e) setHireOpen(false);
          return e;
        }}
        onShow={(id) => { setHireOpen(false); setFilter("all"); void s.refreshQuiet().then(() => focusRow(id)); }}   /* 15.10: сначала свежий список — только что нанятой в старом нет */
        isBound={(id) => !!s.staff.find((r) => r.id === id)?.hasTg}
        newInvite={async (id) => { const r = await s.invite(id); if (r.ok) return r.value; say(r.error, true); return null; }}
        onPoll={s.refreshQuiet} />
      {firing ? (
        <FireDialog row={firing} preview={s.firePreview} fire={s.fire}
          onClose={() => { if (s.staff.find((r) => r.id === firing.id)?.status === "fired") setOpenId(null); setFiring(null); }} />
      ) : null}
      {rebind ? (
        <ConfirmModal tone="warn" title={`Сменить Telegram: ${rebind.name}?`} actionLabel="Отвязать"
          onAction={() => { const r = rebind; setRebind(null); void giveInvite(r, true); }} onCancel={() => setRebind(null)}>
          Прежний Telegram сразу потеряет вход. В карточке появится новая ссылка — её нужно отправить человеку.
        </ConfirmModal>
      ) : null}
      {toast ? <div className={`${css.toast} ${toast.err ? css.toastErr : ""}`} role="status">{toast.t}</div> : null}
    </div>
  );
}
